/**
 * Post-Login Permission Flow
 *
 * This module handles requesting permissions AFTER the user has logged in.
 * This is the App Store/Play Store compliant approach.
 *
 * Flow:
 * 1. User logs in successfully
 * 2. Wait 1 second for the dashboard to load
 * 3. Show push notification pre-prompt (if not already granted)
 * 4. After push notification, show ATT prompt (iOS only)
 *
 * This staged approach ensures:
 * - User understands why permissions are needed
 * - Permissions are requested in context
 * - Better approval rates from users
 * - App Store compliance
 */

import { Capacitor } from '@capacitor/core';
import { requestPushNotificationPermission, checkPushNotificationPermission } from './permissions';
// ATT removed: we do NO cross-app tracking (no IDFA, no ad networks, no data
// brokers), so Apple's ATT framework does not apply to our first-party product
// analytics. We ask our OWN consent question instead and honor the answer.

// Storage key to track if we've completed the post-login permission flow
const POST_LOGIN_PERMISSIONS_KEY = 'insuracentral_post_login_permissions_done';

interface PostLoginPermissionResult {
  pushNotifications: 'granted' | 'denied' | 'skipped';
  tracking: 'granted' | 'denied' | 'skipped' | 'not_required';
}

/**
 * Check if we've already completed the post-login permission flow
 */
export function hasCompletedPostLoginPermissions(): boolean {
  try {
    return localStorage.getItem(POST_LOGIN_PERMISSIONS_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Mark the post-login permission flow as complete
 */
function markPostLoginPermissionsComplete(): void {
  try {
    localStorage.setItem(POST_LOGIN_PERMISSIONS_KEY, 'true');
  } catch {
    // Ignore storage errors
  }
}

/**
 * Run the post-login permission flow
 *
 * Call this after successful login on mobile platforms.
 * It handles the full permission request sequence with proper timing.
 *
 * @param options Configuration options
 * @returns Results of the permission requests
 */
/**
 * First-party analytics consent — replaces the ATT prompt. Asks exactly once
 * (answer persisted); PostHogProvider reads `analytics_consent` and the
 * dispatched event triggers immediate init + identify on "granted".
 * Self-healing: callable on every login, so installs that completed the old
 * ATT-based flow still get asked once.
 */
export async function ensureAnalyticsConsent(): Promise<'granted' | 'denied'> {
  const existing = (() => {
    try { return localStorage.getItem('analytics_consent'); } catch { return null; }
  })();
  if (existing === 'granted' || existing === 'denied') {
    window.dispatchEvent(new Event('analytics-consent-resolved'));
    return existing;
  }
  await new Promise((resolve) => setTimeout(resolve, 500));
  // Native UIAlertController with the standard iOS permission wording —
  // window.confirm only offers stock "Cancel/OK", which read wrong for a
  // consent question.
  let ok = false;
  try {
    const { Dialog } = await import('@capacitor/dialog');
    const { value } = await Dialog.confirm({
      title: 'Help Improve InsuraCentral',
      message:
        'Share usage analytics so we can see which features help agents most. Never used for advertising.',
      okButtonTitle: 'Allow',
      cancelButtonTitle: "Don't Allow",
    });
    ok = value;
  } catch {
    // Plugin unavailable (old binary) — stock confirm still collects consent.
    ok = window.confirm(
      'Help improve InsuraCentral?\n\nShare usage analytics so we can see which features help agents most. Never used for advertising.',
    );
  }
  try { localStorage.setItem('analytics_consent', ok ? 'granted' : 'denied'); } catch { /* noop */ }
  window.dispatchEvent(new Event('analytics-consent-resolved'));
  return ok ? 'granted' : 'denied';
}

export async function runPostLoginPermissionFlow(options?: {
  /** Callback before showing push notification prompt */
  onBeforePushPrompt?: () => void;
  /** Callback after push notification result */
  onAfterPushPrompt?: (granted: boolean) => void;
  /** Callback before showing ATT prompt (iOS only) */
  onBeforeATTPrompt?: () => void;
  /** Callback after ATT result */
  onAfterATTPrompt?: (granted: boolean) => void;
  /** Skip if already completed */
  skipIfCompleted?: boolean;
}): Promise<PostLoginPermissionResult> {
  const {
    onBeforePushPrompt,
    onAfterPushPrompt,
    onBeforeATTPrompt,
    onAfterATTPrompt,
    skipIfCompleted = true,
  } = options || {};

  const result: PostLoginPermissionResult = {
    pushNotifications: 'skipped',
    tracking: 'not_required',
  };

  // Skip if not on native platform
  if (!Capacitor.isNativePlatform()) {
    return result;
  }

  // Skip if already completed
  if (skipIfCompleted && hasCompletedPostLoginPermissions()) {
    console.log('Post-login permissions already completed');
    return result;
  }

  // Wait for the app to settle after login
  await new Promise(resolve => setTimeout(resolve, 1500));

  // Step 1: Push Notifications
  // ── Microphone FIRST: this is a dialing app, and the lazy path meant the
  // native mic prompt interrupted the agent's very first call mid-dial. The
  // pre-prompt UI (PermissionPrompt.tsx) was designed for this and never used.
  try {
    const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
    mic.getTracks().forEach((t) => t.stop()); // permission is the point, not the stream
  } catch {
    // Denied or unavailable — the dialer's own guidance flow handles recovery
    // at call time. Never block login on it.
  }
  await new Promise((resolve) => setTimeout(resolve, 800));

  const pushStatus = await checkPushNotificationPermission();

  if (pushStatus === 'prompt') {
    onBeforePushPrompt?.();

    // Wait a moment before showing system prompt
    await new Promise(resolve => setTimeout(resolve, 500));

    const pushResult = await requestPushNotificationPermission();
    result.pushNotifications = pushResult === 'granted' ? 'granted' : 'denied';

    onAfterPushPrompt?.(pushResult === 'granted');

    // Wait between prompts
    await new Promise(resolve => setTimeout(resolve, 1000));
  } else {
    result.pushNotifications = pushStatus === 'granted' ? 'granted' : 'skipped';
  }

  // Step 2: Analytics consent (first-party; ATT removed — see header note)
  if (Capacitor.isNativePlatform()) {
    onBeforeATTPrompt?.();
    const consent = await ensureAnalyticsConsent();
    result.tracking = consent;
    onAfterATTPrompt?.(consent === 'granted');
  }

  // Mark as complete
  markPostLoginPermissionsComplete();

  console.log('Post-login permission flow complete:', result);
  return result;
}

/**
 * Reset the post-login permission flow (for testing or re-onboarding)
 */
export function resetPostLoginPermissions(): void {
  try {
    localStorage.removeItem(POST_LOGIN_PERMISSIONS_KEY);
  } catch {
    // Ignore storage errors
  }
}
